import taskService, { IUpdateTaskPayload } from "api/taskService";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar } from "@mui/material";
import { axiosInstance } from "api/axios";
import statusService from "api/statusService";
import tagsService from "api/tagsService";
import BinIcon from "assets/icons/bin.svg?react";
import EditIcon from "assets/icons/edite.svg?react";
import Button from "components/Button";
import Chip from "components/Chip";
import HollowButton from "components/HollowButton";
import Input from "components/Input";
import RangePicker from "components/RangePicker";
import RichEditor from "components/RichEditor";
import SearchLabel from "components/SearchLabel";
import SearchMemberNew from "components/SearchMemberNew";
import SelectFlagNew from "components/SelectFlagNew";
import StatusBadge from "components/StatusBadge";
import { IEntity } from "dto/IEntity";
import { IStatus } from "dto/IStatus";
import { ITag } from "dto/ITag";
import { ITask } from "dto/ITask";
import moment from "moment";
import { useContext, useEffect, useMemo, useState } from "react";
import { convertToLetters } from "utils/convertToLetters";
import { formatDate } from "utils/formatDate";
import DeskIcon from "assets/icons/book.svg?react";
import DeskActiveIcon from "assets/icons/desk-active.svg?react";
import deskService from "api/deskService";
import Timeline from "./Timeline";
import ActionsContext from "contexts/ActionsContext";
import { useTranslation } from "react-i18next";

const Task: FC<Props> = ({ task, setTask }) => {
  const { t } = useTranslation();
  const [showEdit, setShowEdit] = useState(false);
  const { data, refetch } = taskService.useGetSingleTask(task.id ?? task.Id);
  const { data: historyData, refetch: refetchHistory } =
    taskService.useGetHistory(task.id ?? task.Id);
  const { mutate: createComment } = taskService.useCreateComment();
  const { data: tagsData } = tagsService.useGetTags();
  const { mutate: removeTask } = taskService.useDeleteTask();
  const tagsList = useMemo(() => tagsData?.data ?? [], [tagsData]);
  const { mutate: updateTask, isLoading } = taskService.useUpdateEvent();
  const { mutate: changeDesk } = deskService.useSetDesk();

  const { data: statusesData } = statusService.useGetStatuses();

  const statused = useMemo(() => statusesData?.data ?? [], [statusesData]);

  const [isHidden, setIsHidden] = useState(false);

  const findedTags: ITag[] = useMemo(
    () =>
      tagsList?.filter((t: ITag) => task?.TagIds?.find((tt) => tt === t.Id)) ??
      [],
    [task, tagsList]
  );

  const [tags, setTags] = useState([]);

  const taskData: ITask = useMemo(() => data?.data ?? [], [data]);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [assigned, setAssigned] = useState<IEntity | null>(null);
  const [, setStatus] = useState<IStatus | null>(null);

  const { tasks, setTasks } = useContext(ActionsContext);

  const handleAddToDesk = () => {
    changeDesk(
      {
        Items: [{ Id: taskData.Id, Kind: 131072 }],
        Show: !taskData.IsDeskActive,
      },
      {
        onSuccess: () => {
          refetch();
          refetchHistory();
        },
      }
    );
  };

  useEffect(() => {
    setTitle(taskData.Title);
    setContent(taskData.Note);
    setStartDate(moment(taskData.FromDate).toDate());
    setEndDate(moment(taskData.ToDate).toDate());
  }, [taskData]);

  useEffect(() => {
    const findedStatuses = statused?.find(
      (status: IStatus) => taskData?.StatusId === status.Id
    );
    setStatus(findedStatuses);
  }, [taskData, statused]);

  useEffect(() => {
    setTags(findedTags);
  }, [findedTags]);

  useEffect(() => {
    refetchHistory();
  }, [task]);

  const [comment, setComment] = useState("");

  const handleUpdate = () => {
    const payload: IUpdateTaskPayload = {
      Allday: taskData.AllDay,
      FromDate: taskData.FromDate,
      Id: taskData.Id,
      IsFavourite: taskData.IsFavourite,
      Note: content,
      OwnerId: taskData.OwnerId,
      OwnerKind: taskData.Kind,
      TagIds: tags.map((t) => t.Id),
      ToDate: taskData.ToDate,
      Title: title,
    };

    // if(status) {
    //   payload.s
    // }

    updateTask(payload, {
      onSuccess: () => {
        console.log("aktualizuje");
        refetchHistory();
        setShowEdit(false);
        const copy = [...tasks].map((t) => {
          if (t.Id === taskData.Id || t.id === taskData.Id) {
            t.title = title;
            t.Title = title;
            t.Note = content;
            t.description = content;
          }

          return t;
        });

        setTasks(copy);
      },
    });
  };

  const handleSendComment = () => {
    createComment(
      {
        TaskId: task.Id,
        Note: comment,
      },
      {
        onSuccess: () => {
          refetchHistory();
        },
      }
    );
  };

  useEffect(() => {
    const item = localStorage.getItem("timeline_state");

    if (!item) return;

    const state = item === "true" ? true : false;
    setIsHidden(state);
  }, []);

  const handleUpdateTimelineState = (newState: boolean) =>
    localStorage.setItem("timeline_state", String(newState));

  return (
    <div className={styles.wrapper}>
      <div className={styles.wrap}>
        <div className={`${styles.header} ${showEdit && styles.headerLines}`}>
          {showEdit && (
            <>
              <div className={styles.controls}>
                <RangePicker
                  allowOneDate
                  defaultDates={
                    startDate && endDate ? [startDate, endDate] : undefined
                  }
                  onAccept={(dateRange) => {
                    setStartDate(dateRange[0]!.toDate());
                    setEndDate(dateRange[1]!.toDate());
                  }}
                />
                <SelectFlagNew />
                <SearchLabel
                  label={t("settings.tabs.labels")}
                  onChoose={(tag) => {
                    const isExist = tags.find((t) => t.Name === tag?.Name);
                    if (isExist) return;
                    setTags([...tags, tag!]);
                  }}
                />
                <SearchMemberNew
                  onChoose={(member) => {
                    axiosInstance.post("/task/pass", {
                      Id: taskData.Id,
                      UserId: member.UserId,
                    });
                    setAssigned(member);
                  }}
                />
              </div>
              <div className={styles.lists}>
                <Chip element={<>{formatDate([startDate, endDate])}</>} />
                {tags.map((tag) => (
                  <Chip
                    key={tag.Id}
                    element={<>{tag.Name}</>}
                    onDelete={() => {
                      setTags(tags.filter((t) => t.Id !== tag.Id));
                    }}
                  />
                ))}
                {assigned && (
                  <Chip
                    key={assigned.Label ?? ""}
                    avatar={
                      <Avatar className={styles.avatar}>
                        {convertToLetters(assigned.Label ?? "")}
                      </Avatar>
                    }
                    element={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        {assigned.Label ?? ""}
                      </div>
                    }
                  />
                )}
              </div>
            </>
          )}
          <div className={styles.headerLine}>
            {!showEdit && <div className={styles.title}>{title ?? ""}</div>}
            {showEdit && (
              <Input
                label={t("forms.title")}
                value={title}
                onChange={(val) => setTitle(val)}
              />
            )}
            {!showEdit && (
              <div className={styles.icons}>
                <div className={styles.iconBox}>
                  <EditIcon
                    onClick={() => setShowEdit(true)}
                    className={styles.icon}
                  />
                </div>
                <div className={styles.iconBox}>
                  {taskData?.IsDeskActive ? (
                    <DeskActiveIcon
                      onClick={handleAddToDesk}
                      className={styles.icon}
                    />
                  ) : (
                    <DeskIcon
                      onClick={handleAddToDesk}
                      className={styles.icon}
                    />
                  )}
                </div>
                <div className={styles.iconBox}>
                  <BinIcon
                    onClick={() => {
                      removeTask(
                        { Id: task.Id, MoveToTrash: true },
                        {
                          onSuccess: () => {
                            setTask(null);
                            setTasks(
                              [...tasks].filter((a) => a.Id !== task.Id)
                            );
                          },
                        }
                      );
                    }}
                    className={styles.icon}
                  />
                </div>
              </div>
            )}
          </div>
          <div className={styles.headerLine}>
            <div className={styles.tags}>
              {task.StatusId && !showEdit && (
                <StatusBadge
                  small
                  showName={true}
                  status={{
                    Color: 6,
                    Id: 1,
                    Name: t("tasks.completed"),
                    Type: 1,
                    Global: true,
                    WorkspaceId: "",
                  }}
                />
              )}
              {!showEdit &&
                tags.map((t) => <Chip small element={<>{t.Name}</>} />)}
            </div>
            {!showEdit && (
              <div className={styles.time}>
                <Avatar className={styles.avatar}>
                  {convertToLetters(data?.data?.CreatedBy ?? "")}
                </Avatar>
                {data?.data?.CreatedBy} | {t("tasks.createdAt")} - {""}
                {moment(task.FromDate).format("DD.MM.YY HH:mm")}
              </div>
            )}
          </div>
        </div>
        <div className={styles.big}>
          {!showEdit && (
            <div
              dangerouslySetInnerHTML={{ __html: content ?? "" }}
              className={`${styles.content} ${isHidden && styles.expandedSize}`}
            ></div>
          )}
          <div className={styles.object}>
            {!showEdit && <div className={styles.lineD}></div>}
            {showEdit && (
              <div className={styles.editore}>
                <RichEditor content={content} setContent={setContent} />
              </div>
            )}
            {!showEdit && (
              <div className={styles.timelineHeader}>
                <div className={styles.timelineText}>
                  {!!historyData?.data.Unread && (
                    <div className={styles.dote}>
                      {historyData?.data?.Unread}
                    </div>
                  )}
                  <div>{t("tasks.timeline")}</div>
                </div>
                <div
                  onClick={() => {
                    handleUpdateTimelineState(!isHidden);
                    setIsHidden(!isHidden);
                  }}
                  className={styles.expanded}
                >
                  {!isHidden ? t("common.collapse") : t("common.expand")}{" "}
                  <FontAwesomeIcon icon={isHidden ? faCaretDown : faCaretUp} />
                </div>
              </div>
            )}
            {!showEdit && (
              <div
                className={`${styles.timeline} ${isHidden && styles.hidden}`}
              >
                <div className={styles.list}>
                  <Timeline historyData={historyData} />
                </div>
              </div>
            )}
            {!showEdit && !isHidden && (
              <>
                <div className={styles.commentEditor}>
                  <div className={`${styles.editor}`}>
                    <RichEditor setContent={setComment} content={comment} />
                  </div>
                </div>
                <div
                  style={{
                    background: "#F4EFF4",
                    paddingBottom: "8px",
                    paddingRight: "8px",
                    paddingTop: "8px",
                    borderTop: "solid 1px #e5e0ec",
                    width: "calc(100% - 24px)",
                    marginLeft: "12px",
                  }}
                  className={styles.btnWraper}
                >
                  <Button
                    className={styles.btn}
                    onClick={() => {
                      handleSendComment();
                    }}
                    text={t("common.add")}
                  />
                </div>
              </>
            )}
            {showEdit && (
              <div
                style={{
                  marginTop: "12px",
                  paddingTop: "12px",
                  borderTop: "solid 1px #e5e0ec",
                  paddingLeft: 0,
                  paddingRight: "12px",
                  marginLeft: 0,
                }}
                className={styles.buttons}
              >
                <HollowButton
                  onClick={() => setShowEdit(false)}
                  text={t("common.cancel")}
                />
                <Button
                  loading={isLoading}
                  onClick={() => {
                    handleUpdate();
                  }}
                  text={t("common.save")}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Task;
